import { makeRouter } from '@adornis/router/client/make-router.js';
import React, { useEffect, useState } from 'react'; // <-- React hier hinzugefügt
import { AuthModal } from './app/auth/auth-modal.js';
import AdminPage from './app/admin/page.js';
import ResetPasswordPage from './app/auth/reset-password/page.js';
import LoginPage from './app/auth/sign-in/page.js';
import SignUpPage from './app/auth/sign-up/page.js';
import Home from './app/page.js';
import StudentExamPage from './app/student-exam/page.js';
import JoinLandingPage from './app/join/page.js';
import { ImpersonationBanner } from './components/impersonation-banner.js';
import { getGuestUserID, incrementGuestMessageCount } from './lib/guest-user-id.js';
import { useCurrentUser } from './lib/useCurrentUser.js';
import { cn } from './lib/utils.js';
import { getTeacherByUserIdQuery } from '../db/api/get-teacher.js';

const GUEST_AUTH_NUDGE_THRESHOLD = 5;

function Test() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-8">
      <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
        <h2 className="text-2xl font-bold text-slate-900">Test route</h2>
        <p className="mt-2 text-slate-600">This route is intentionally minimal while the main app loads.</p>
      </div>
    </div>
  );
}

/**
 * Auth guard component: shows children only when logged in,
 * otherwise redirects to login page.
 */
function AuthGuard({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useCurrentUser();

  // State, der sich nur für diese Sitzung merkt, ob man Gast ist
  const [isGuest, setIsGuest] = useState(true);

  // NEU: States um das Modal von innen heraus zu steuern
  const [forceModalOpen, setForceModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'login' | 'signup'>('login');
  const [showGuestNudge, setShowGuestNudge] = useState(false);
  const [hasShownGuestNudge, setHasShownGuestNudge] = useState(false);
  const guestUserID = getGuestUserID();

  // // Registriere Cleanup-Handler für Guest-Daten bei Seitenwechsel
  // useEffect(() => {
  //   const unsubscribe = registerGuestCleanupHandler(guestUserID);
  //   return unsubscribe;
  // }, [guestUserID]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-white dark:bg-background">
        <div className="text-slate-500 dark:text-slate-400 text-lg">Laden…</div>
      </div>
    );
  }

  const hasAccess = (user || isGuest) && !forceModalOpen && !showGuestNudge;

  const childrenWithProps = React.Children.map(children, child => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child as React.ReactElement<any>, {
        onOpenLogin: () => {
          setModalMode('login');
          setForceModalOpen(true);
          setShowGuestNudge(false);
        },
        onOpenSignup: () => {
          setModalMode('signup');
          setForceModalOpen(true);
          setShowGuestNudge(false);
        },
        onGuestMessageSent: (conversationId: string) => {
          if (user) return;
          const nextCount = incrementGuestMessageCount(conversationId);
          if (!hasShownGuestNudge && nextCount >= GUEST_AUTH_NUDGE_THRESHOLD) {
            setShowGuestNudge(true);
            setHasShownGuestNudge(true);
          }
        },
      });
    }
    return child;
  });

  return (
    <div className="relative w-full h-screen overflow-hidden bg-slate-50 dark:bg-background">
      {/* HINTERGRUND */}
      <div
        className={cn(
          'h-full w-full transition-all duration-700 ease-in-out',
          !hasAccess
            ? 'blur-md opacity-60 scale-[0.98] pointer-events-none select-none'
            : 'blur-0 opacity-100 scale-100',
        )}
      >
        {childrenWithProps}
      </div>

      {/* VORDERGRUND */}
      {!hasAccess && (
        <div className="absolute inset-0 z-[100] flex items-center justify-center bg-slate-900/10 backdrop-blur-[2px] p-4">
          {showGuestNudge && !forceModalOpen ? (
            <div className="w-full max-w-md bg-white dark:bg-card rounded-2xl shadow-2xl p-8 border border-slate-200 dark:border-slate-700 animate-in fade-in zoom-in-95 duration-300">
              <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100">
                Hi, danke dass du Gradias testest
              </h2>
              <p className="text-sm text-slate-600 dark:text-slate-400 mt-3 leading-relaxed">
                Wenn du dich anmeldest, kannst du deine Chats speichern und nahtlos weiterarbeiten.
              </p>
              <div className="mt-6 flex items-center justify-center gap-4 text-sm">
                <button
                  type="button"
                  onClick={() => {
                    setModalMode('login');
                    setShowGuestNudge(false);
                    setForceModalOpen(true);
                  }}
                  className="text-brand-500 font-semibold hover:underline"
                >
                  Anmelden
                </button>
                <span className="text-slate-300 dark:text-slate-600">|</span>
                <button
                  type="button"
                  onClick={() => {
                    setModalMode('signup');
                    setShowGuestNudge(false);
                    setForceModalOpen(true);
                  }}
                  className="text-brand-500 font-semibold hover:underline"
                >
                  Registrieren
                </button>
              </div>
              <div className="mt-5 text-center">
                <button
                  type="button"
                  onClick={() => setShowGuestNudge(false)}
                  className="text-sm text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 transition-colors"
                >
                  Später
                </button>
              </div>
            </div>
          ) : (
            <AuthModal
              initialMode={modalMode}
              onAuthSuccess={() => {
                setIsGuest(false);
                setForceModalOpen(false);
                setShowGuestNudge(false);
              }}
              onContinueAsGuest={() => {
                setIsGuest(true);
                setForceModalOpen(false);
              }}
            />
          )}
        </div>
      )}
    </div>
  );
}

function AdminGuard({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useCurrentUser();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);

  useEffect(() => {
    const userId = (user as any)?._id;
    if (!userId) return;
    void getTeacherByUserIdQuery(userId)({ isAdmin: 1 })
      .then((teachers: any[]) => {
        setIsAdmin(teachers?.[0]?.isAdmin === true);
      })
      .catch(() => setIsAdmin(false));
  }, [(user as any)?._id]);

  if (isLoading || isAdmin === null) {
    return (
      <div className="flex items-center justify-center h-screen bg-white">
        <div className="text-slate-500 text-lg">Laden…</div>
      </div>
    );
  }

  if (!user || !isAdmin) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-50">
        <div className="text-slate-500 text-lg">Kein Zugriff.</div>
      </div>
    );
  }

  return <>{children}</>;
}

export const router = makeRouter({
  home: {
    path: '/',
    render: () => (
      <AuthGuard>
        <Home />
      </AuthGuard>
    ),
  },
  resetPassword: {
    path: '/auth/reset-password',
    render: () => <ResetPasswordPage />,
  },
  signIn: {
    path: '/auth/sign-in',
    render: () => <LoginPage />,
  },
  signUp: {
    path: '/auth/sign-up',
    render: () => <SignUpPage />,
  },
  join: {
    path: '/join',
    render: () => <JoinLandingPage />,
  },
  test: {
    path: '/test',
    render: () => <Test />,
  },
  /**
   * The student-facing exam page. Deliberately NOT wrapped in `AuthGuard`: that guard lets guests through
   * (`hasAccess = (user || isGuest)`) and blurs the page behind a login modal rather than gating it, which
   * is the wrong shape here. The page does its own "not signed in" state, and the real access decision is
   * server-side — `getStudentExamQuery` is scoped by the row-level publishFilter.
   */
  studentExam: {
    path: '/student-exam/:examId',
    render: params => <StudentExamPage examId={params.examId ?? ''} />,
  },
  admin: {
    path: '/admin',
    render: () => (
      <AdminGuard>
        <AdminPage />
      </AdminGuard>
    ),
  },
});
router.init();

export function App() {
  const [outlet, setOutlet] = useState<React.ReactNode>(router.outlet.value);

  useEffect(() => {
    // Subscribe to the Preact Signal so React re-renders on route changes
    const unsubscribe = router.outlet.subscribe((value: React.ReactNode) => {
      setOutlet(value);
    });
    return unsubscribe;
  }, []);

  return (
    <>
      <ImpersonationBanner />
      {outlet}
    </>
  );
}
